import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { EQuerySubmittedResponse } from '../../models/e-query-submitted-response';
import { EQueryCCQuestion } from '../../../admin/e-query/models/e-query-c-c-question';
import { FormControl, FormGroup } from '@angular/forms';
import { maxLengthInBytes } from '../../../shared/validators';
import { EQueryResponse, EQueryResponsePerQuestion } from '../../models/e-query-response';
import { FileToLanguage } from '../../../covance-file-upload/covance-file-upload.component';
import { Settings } from '../../../core';
import { FileFormArray } from '../../../covance-file-upload/file-form-array';
import { EQueryResponseAttachmentsPerQuestion } from '../../models/e-query-response-attachments-per-question';
import { CultureInfo } from '../../../core/culture-info';
import { EQueryStatus } from '../../models/e-query-status';
import { combineLatest, Subscription } from 'rxjs';

@Component({
    selector: 'open-questions',
    template: require('./open-questions.component.html'),
    styles: [require('./open-questions.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class OpenQuestionsComponent implements OnDestroy {
    @Output()
    public downloadAttachmentClick = new EventEmitter<number>();

    @Output()
    public previewAttachmentClick = new EventEmitter<number>();

    @Input()
    public status: string;

    @Input()
    public set shouldBeResponded(value: boolean) {
        this.shouldBeRespondedValue = value;
        this.createQuestionsControls();
    }

    public get shouldBeResponded() {
        return this.shouldBeRespondedValue;
    }

    @Input()
    public set questions(value: EQueryCCQuestion[]) {
        this.questionsValue = value;
        this.createQuestionsControls();
        this.createResponseQuestionMap();
    }

    public get questions() {
        return this.questionsValue;
    }

    @Input()
    public set responses(value: EQuerySubmittedResponse[]) {
        this.responsesValue = value;
        this.createResponseQuestionMap();
    }

    public get responses() {
        return this.responsesValue;
    }

    public get wasAtLeastOneResponseAdded() {
        let result = false;
        this.questionControls.forEach((valueForm, keyQuestion) => {
            if (valueForm.value && String(valueForm.value).trim()) {
                result = true;
            }
        });
        return result;
    }

    public responseQuestionMap: Map<EQueryCCQuestion, EQuerySubmittedResponse[]> = new Map<EQueryCCQuestion, EQuerySubmittedResponse[]>();
    public attachmentsQuestionMap: Map<number, string[]> = new Map<number, string[]>();
    public editorForm: FormGroup;
    public questionControls: Map<EQueryCCQuestion, FormControl>;
    public questionFileFormArrays: Map<EQueryCCQuestion, FileFormArray>;
    public readonly allowedExtensions: string[];
    public readonly eQueryMaxSizeOfAttachments: number;
    public readonly maxFilesCountPerIssue: number;
    public textMaxBytes = 2050;
    public openStatus = EQueryStatus.Open;
    public hasDuplicateFile = false;
    public duplicateFileNames = '';

    private selectedFilesMap: Map<number, FileToLanguage>;
    private questionsValue: EQueryCCQuestion[];
    private responsesValue: EQuerySubmittedResponse[];
    private shouldBeRespondedValue = false;
    private subscription: Subscription;

    constructor(
        private readonly settings: Settings,
    ) {
        this.allowedExtensions = settings.eQueryAttachmentsExtensions;
        this.eQueryMaxSizeOfAttachments = settings.eQueryMaxSizeOfAttachments;
        this.questionControls = new Map<EQueryCCQuestion, FormControl>();
        this.questionFileFormArrays = new Map<EQueryCCQuestion, FileFormArray>();
        this.attachmentsQuestionMap = new Map<number, string[]>();
        this.selectedFilesMap = new Map<number, FileToLanguage>();
        this.maxFilesCountPerIssue = settings.eQueryMaxNumberOfAttachmentsPerIssue;
    }

    public createResponseQuestionMap(): void {
        if (!this.questionsValue || !this.responsesValue) {
            this.responseQuestionMap = new Map<EQueryCCQuestion, EQuerySubmittedResponse[]>();
            return;
        }
        this.responses.forEach(response => {
            const relatedQuestion = this.questionsValue.find(question => question.questionId === response.questionId);
            if (!relatedQuestion) {
                return;
            }
            if (this.responseQuestionMap.has(relatedQuestion)) {
                this.responseQuestionMap.get(relatedQuestion).push(response);
            } else {
                this.responseQuestionMap.set(relatedQuestion, [response]);
            }
        });
    }

    public createEQueryResponse(): EQueryResponse {
        const responses: EQueryResponsePerQuestion[] = [];
        this.pushQuestionResponses(responses);
        const attachments = this.createQuestionAttachmentsArray();
        let files: FileToLanguage = new Map<File, CultureInfo>();
        this.selectedFilesMap.forEach((fileValue, keyNumber) => {
            files = new Map([...files, ...fileValue]);
        });
        return {responses: responses, attachmentsPerQuestionList: attachments, files: files};
    }

    public onFilesChanged(selectedFiles: FileToLanguage, question: EQueryCCQuestion) {
        if (this.attachmentsQuestionMap.has(question.questionId)) {
            this.attachmentsQuestionMap.delete(question.questionId);
        }
        if (this.selectedFilesMap.has(question.questionId)) {
            this.selectedFilesMap.delete(question.questionId);
        }
        const filesArray = Array.from(selectedFiles.keys());
        const fileNamesArray = filesArray.map(file => file.name);
        this.attachmentsQuestionMap.set(question.questionId, fileNamesArray);
        this.selectedFilesMap.set(question.questionId, selectedFiles);
        this.checkHasDuplicateFile();
        this.validateFileControls();
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public createQuestionIssueLabel(question: EQueryCCQuestion): string {
        if (!question || (!question.issueType && !question.issueDescription)) {
            return '';
        }
        let resultLabel = '';
        if (question.issueType) {
            resultLabel = question.issueType;
        }
        if (question.issueDescription) {
            resultLabel = resultLabel ? `${resultLabel}/${question.issueDescription}` : question.issueDescription;
        }

        return  resultLabel;
    }

    private createQuestionsControls(): void {
        if (!this.shouldBeRespondedValue || !this.questionsValue) {
            return;
        }
        this.questionsValue.forEach(question => {
            const control = new FormControl();
            const fileFormArray = new FileFormArray(false);
            this.setQuestionControlValidation(control);
            this.questionControls.set(question, control);
            this.subscription = combineLatest(control.valueChanges).subscribe(() => {
                if (!this.editorForm) {
                    return;
                }
                this.validateFileControls();
            });
            this.questionFileFormArrays.set(question, fileFormArray);
            if (this.editorForm) {
                this.editorForm.addControl(String(question.questionId), control);
            } else {
                this.editorForm = new FormGroup({
                    [String(question.questionId)]: control
                });
            }
            this.editorForm.addControl(`files_${question.questionId}`, fileFormArray);
        });
        if (this.editorForm) {
            this.editorForm.updateValueAndValidity({onlySelf: false});
        }
    }

    private setQuestionControlValidation(control: FormControl) {
        control.setValidators([maxLengthInBytes(this.textMaxBytes)]);
        control.updateValueAndValidity();
    }

    private pushQuestionResponses(responses: EQueryResponsePerQuestion[]): void {
        this.questionControls.forEach((formControl, keyQuestion) => {
            const wasResponded = (formControl.value && formControl.value.toString().trim())
                || this.attachmentsQuestionMap.has(keyQuestion.questionId);
            if (wasResponded) {
                responses.push({
                    questionId: keyQuestion.questionId,
                    responseText: formControl.value ? formControl.value.toString().trim() : ''
                });
            }
        });
    }

    private checkHasDuplicateFile() {
        this.hasDuplicateFile = false;
        this.duplicateFileNames = '';
        let duplicateFileNamesSet = new Set<string>();
        let fileNamesSet = new Set<string>();
        Array.from(this.attachmentsQuestionMap.values())
            .map(names => {
                names.forEach(name => {
                    if (fileNamesSet.has(name)) {
                        duplicateFileNamesSet.add(name);
                        this.hasDuplicateFile = true;
                    }
                    fileNamesSet.add(name);
                });
            });
        this.duplicateFileNames = Array.from(duplicateFileNamesSet.values()).join(', ');
    }

    private createQuestionAttachmentsArray(): EQueryResponseAttachmentsPerQuestion[] {
        let result: EQueryResponseAttachmentsPerQuestion[] = [];
        this.attachmentsQuestionMap.forEach((namesArrayValue, keyQuestionId) => {
            result.push({questionId: keyQuestionId, attachmentNames: namesArrayValue});
        });
        return result;
    }

    private validateFileControls() {
        const textControls = Object.keys(this.editorForm.controls)
            .filter(s => !s.includes('files'))
            .map(s => ({key: s, value: this.editorForm.controls[s].value as string}));

        textControls.forEach(textControl => {
            const fileControl = this.editorForm.controls[`files_${textControl.key}`];
            if ((fileControl.value as File[]).length > 0) {
                if (!textControl.value) {
                    fileControl.setErrors({fileError: true});
                } else {
                    fileControl.setErrors({fileError: false});
                    fileControl.updateValueAndValidity();
                }
            }
        });
    }
}
